/**
 * Plan-year financial: Ops-side entity for the Financials tab's authoring
 * side (Rates & Fees).
 *
 * Mirrors the four migration-008 layers the BFF serves from
 * packages/bff/src/finmon/{planYearFinancial,planYearRates,census}.ts through
 * packages/bff/src/finmon/planYearRoutes.ts. Ops keeps its own copy of the
 * shapes rather than importing from `@finance`: modules do not import each
 * other in this repo, and the same duplication already exists for
 * OpsFinancials. The canonical finance-side mirror is
 * packages/finance/src/api/finance.ts; keep the two in step.
 *
 * MASKING. Broker commission is commission economics. The BFF hides the rate
 * unless the caller holds `finance.commissions`, and when it does, the
 * coverage line's grand total comes back NULL rather than a total that quietly
 * excludes the broker line. `isWithheld` below is what lets the screen render
 * that as withheld rather than missing, which is the difference between an
 * admin PEPM that reads as gated and one that reads as wrong.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffFetch } from '@shared/api';
import type { PriorYearFigures } from './priorYear';

// ---------------------------------------------------------------------------
// Vocabularies (the SQL domains in 008_plan_year_financial.sql)
// ---------------------------------------------------------------------------

export type FeeBasis =
  | 'pepm' | 'pmpm' | 'per_script' | 'flat' | 'pct_utilization'
  | 'volume_tiered' | 'hourly' | 'pct_of_savings' | 'pct_of_billed_charges'
  // Migration 018, from the Rx book. cost_plus carries the PLUS in `rate`
  // (20 = cost plus 20%), the same way pct_of_savings carries its percentage.
  | 'per_claim' | 'cost_plus';

export type SettledVia =
  | 'admin_pepm' | 'claims' | 'third_party' | 'direct' | 'included' | 'not_included';

export type CoverageLine =
  | 'medical' | 'rx' | 'dental' | 'vision' | 'hsa' | 'fsa' | 'life' | 'disability' | 'other';

export type RateTier = 'ee' | 'es' | 'ec' | 'ee_plus_one' | 'fam' | 'composite' | 'total';

export type RateColumn = 'total_rate' | 'admin_rate' | 'spec_rate' | 'agg_rate' | 'funding_rate';

export type LifecycleStatus = 'believed' | 'confirmed' | 'locked';
export type FundingMethod = 'traditional' | 'simply_funded' | 'fully_insured' | 'unknown';
export type BudgetBasis = 'fixed_at_renewal' | 'current_census' | 'budgeted_census' | 'unknown';
export type BreakoutStatus =
  | 'draft' | 'pricing_at_proposal' | 'proposed' | 'pending_review' | 'approved' | 'locked';

export const TIER_ORDER: RateTier[] = ['ee', 'es', 'ec', 'ee_plus_one', 'fam', 'composite', 'total'];

export const TIER_LABELS: Record<RateTier, string> = {
  ee: 'EE', es: 'ES', ec: 'EC', ee_plus_one: 'EE+1', fam: 'FAM',
  composite: 'Composite', total: 'Total',
};

export const BASIS_LABELS: Record<FeeBasis, string> = {
  pepm: 'PEPM', pmpm: 'PMPM', per_script: 'Per script', flat: 'Flat monthly',
  pct_utilization: '% of utilization', volume_tiered: 'Volume tiered',
  hourly: 'Hourly', pct_of_savings: '% of savings',
  pct_of_billed_charges: '% of billed charges',
  per_claim: 'Per claim', cost_plus: 'Cost plus %',
};

export const SETTLED_VIA_LABELS: Record<SettledVia, string> = {
  admin_pepm: 'Admin PEPM', claims: 'Through claims', third_party: 'Third party',
  direct: 'Billed direct', included: 'Included', not_included: 'Not included',
};

export const COVERAGE_LINE_LABELS: Record<CoverageLine, string> = {
  medical: 'Medical', rx: 'Rx', dental: 'Dental', vision: 'Vision', hsa: 'HSA',
  fsa: 'FSA', life: 'Life', disability: 'Disability', other: 'Other',
};

export const BREAKOUT_STATUS_LABELS: Record<BreakoutStatus, string> = {
  draft: 'Draft', pricing_at_proposal: 'Pricing at proposal', proposed: 'Proposed',
  pending_review: 'Pending review', approved: 'Approved', locked: 'Locked',
};

export const FUNDING_METHOD_LABELS: Record<FundingMethod, string> = {
  traditional: 'Traditional', simply_funded: 'Simply funded',
  fully_insured: 'Fully insured', unknown: 'Not set',
};

export const BUDGET_BASIS_LABELS: Record<BudgetBasis, string> = {
  fixed_at_renewal: 'Fixed at renewal', current_census: 'Current census',
  budgeted_census: 'Budgeted census (hand-kept)', unknown: 'Not set',
};

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export interface PlanYearFinancial {
  id: string;
  accountId: string;
  contractYearId: string;
  groupConfigId: string | null;
  groupLabel: string;
  planYearLabel: string | null;
  fundingMethod: FundingMethod;
  budgetBasis: BudgetBasis;
  isCaptive: boolean;
  /** DEPRECATED for resolution (014): the proposal wording, kept as a record. */
  scenario: string | null;
  scenarioOptions: unknown | null;
  /** The year default arrangement. Per-plan declarations override it. */
  pricingScenarioId: string | null;
  livesAtProposal: number | null;
  bookRate: number | null;
  breakoutStatus: BreakoutStatus;
  breakoutStatusNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  status: LifecycleStatus;
  lockedBy: string | null;
  lockedAt: string | null;
  source: string;
  notes: string | null;
  updatedAt: string;
  /** Catalog services elected when the stamp last ran; null = never stamped. */
  electedServiceIds: string[] | null;
  electedServicesAt: string | null;
}

export type StampRule = 'always' | 'on_service' | 'manual';

/**
 * The book-wide component vocabulary (migration 009). The Ops breakout picks
 * from this rather than accepting free text: across ~140 groups free text would
 * produce three spellings of "TPA Fee" and no book-wide question would ever be
 * answerable from it.
 */
export interface FeeComponent {
  id: string;
  componentKey: string;
  label: string;
  coverageLine: CoverageLine;
  defaultBasis: FeeBasis;
  defaultSettledVia: SettledVia;
  defaultBillingLine: string | null;
  defaultVendor: string | null;
  catalogServiceId: string | null;
  isCommission: boolean;
  stampRule: StampRule;
  displayOrder: number;
  active: boolean;
  notes: string | null;
}

export interface BreakoutComponent {
  id: string;
  planYearFinancialId: string;
  /** The vocabulary row this was picked from; null on an "Other". */
  feeComponentId: string | null;
  componentKey: string | null;
  /** Declared by the vocabulary; null on an "Other". */
  isCommission?: boolean | null;
  /**
   * The arrangement this priced row belongs to (migration 014). NULL is the
   * common case and means the component does not vary by arrangement, so it
   * counts in every one of them.
   */
  pricingScenarioId?: string | null;
  scenarioKey?: string | null;
  scenarioLabel?: string | null;
  groupServiceId: string | null;
  catalogServiceId: string | null;
  feeTemplateId: string | null;
  feeLineId: string | null;
  component: string;
  vendor: string | null;
  coverageLine: CoverageLine;
  basis: FeeBasis;
  rate: number | null;
  rateTiers: unknown | null;
  rateText: string | null;
  settledVia: SettledVia;
  contributesToPepm: boolean;
  billingLine: string | null;
  destVendor: number | null;
  destTpa: number | null;
  destVeracity: number | null;
  /**
   * Broker commission carved out of THIS fee rather than billed as its own line
   * (018, from the Rx book). Commission economics: withheld like the rate.
   */
  destBroker: number | null;
  /**
   * Who INVOICES the group, which is neither `vendor` (who gets paid) nor
   * `billingLine` (which invoice line it rolls into). Decides whose remittance
   * a payment should turn up on.
   */
  billedBy: string | null;
  billedByPartnerId: string | null;
  /**
   * A broker slice sits on this row but is withheld from you (018). Distinct
   * from `masked`, which withholds the whole rate: the fee itself is ordinary
   * money the group is billed.
   */
  brokerSliceWithheld?: boolean;
  priorYearRate: number | null;
  status: LifecycleStatus;
  source: string;
  displayOrder: number;
  notes: string | null;
  /** True when the rate is hidden by the commission gate rather than absent. */
  masked?: boolean;
  /**
   * What this row's LINKED card resolves to at the year's lives (021).
   * Compared against the row's own picked card, so an off-cycle row that
   * matches its card does not read as a deviation.
   */
  cardRate?: number | null;
  cardEffectiveFrom?: string | null;
  cardEffectiveTo?: string | null;
  /** The linked card's window misses the year's as-of date: a deliberate
   *  re-pull from a different period. Derived server-side, never stored. */
  offCycle?: boolean;
  /** rate differs from cardRate, both non-null: the adjustment signal. */
  deviatesFromCard?: boolean;
}

export interface RateSetTierRow {
  id: string;
  tier: RateTier;
  totalRate: number | null;
  adminRate: number | null;
  specRate: number | null;
  aggRate: number | null;
  fundingRate: number | null;
  enteredColumns: RateColumn[];
  derivedColumns: RateColumn[];
  identityDelta: number | null;
  identityStatus: 'ok' | 'mismatch' | 'derived' | 'underdetermined' | 'empty' | string;
  displayOrder: number;
  notes: string | null;
}

export interface RateSet {
  id: string;
  planYearFinancialId: string;
  label: string;
  scope: 'plan' | 'plan_group' | 'coverage_line' | 'group';
  planIds: string[];
  planKeys: string[];
  coverageLine: CoverageLine;
  effectiveFrom: string;
  effectiveTo: string | null;
  supersedesId: string | null;
  changeReason: string | null;
  status: LifecycleStatus;
  source: string;
  notes: string | null;
  /**
   * The partner's Plan ID this set prices (022): a vba_partnerplanref pick
   * from the Partner ID registry, beside the label, never instead of it.
   * `partnerPlanIdValue` is the org-independent join key census member counts
   * arrive under. All nullable and nothing gates on them: an unset Plan ID is
   * a gap, not a blocker (Sasha 9/02).
   */
  partnerPlanRefId: string | null;
  partnerPlanRefOrg: string | null;
  partnerPlanIdValue: string | null;
  partnerPlanLabel: string | null;
  tiers: RateSetTierRow[];
}

export interface CaptiveTerms {
  id: string;
  planYearFinancialId: string;
  captiveName: string | null;
  managementFee: number | null;
  managementFeeCadence: string;
  collateral: number | null;
  collateralCadence: string;
  expectedFirstBilledMonth: string | null;
  inBudgetRates: boolean | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: LifecycleStatus;
  source: string;
  notes: string | null;
}

export interface CoverageTotals {
  coverageLine: CoverageLine;
  exBroker: number | null;
  broker: number | null;
  grandTotal: number | null;
  offPepm: number | null;
  unresolved: string[];
  nonPepmBases: string[];
  /**
   * "Breakout of Gross Fee", EX BROKER, so the three tie to exBroker rather
   * than to the grand total. Withheld with exBroker, since they sum to it.
   */
  destVendor: number | null;
  destTpa: number | null;
  destVeracity: number | null;
  /** Broker slices inside ordinary fees. Withheld with the broker total. */
  destBroker: number | null;
}

export interface BillingInstruction {
  billingLine: string | null;
  amount: number | null;
  components: string[];
}

/** CoverageTotals for one arrangement (014/020): what a tab's footer shows. */
export interface ScenarioTotals extends CoverageTotals {
  pricingScenarioId: string;
  scenarioLabel: string | null;
}

/**
 * A breakout tab (migration 020): one arrangement this plan year works, with a
 * user-typed display name, its own status ladder, book rate and approval. The
 * tab IS the pricing-scenario axis wearing a name; it owns no rows. Zero tabs
 * = the year renders as the single un-tabbed table it always has.
 */
export interface BreakoutTab {
  id: string;
  planYearFinancialId: string;
  pricingScenarioId: string;
  scenarioKey: string;
  scenarioLabel: string;
  displayName: string;
  /** The tab's own book rate; the header's is the un-tabbed legacy fallback. */
  bookRate: number | null;
  status: BreakoutStatus;
  statusNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  archived: boolean;
  /** 0 until the first version mints. The tab label reads "Cigna v3". */
  latestVersionNo: number;
  /** The FINAL APPROVED snapshot's number, when one exists. */
  finalVersionNo: number | null;
  updatedAt: string;
}

export interface BreakoutVersionMeta {
  id: string;
  planYearScenarioId: string;
  versionNo: number;
  label: string | null;
  isFinal: boolean;
  createdBy: string | null;
  createdAt: string;
}

/** What a version froze. Figures arrive masked unless the caller sees commissions. */
export interface BreakoutSnapshot {
  displayName: string;
  pricingScenarioId: string;
  status: BreakoutStatus;
  statusNote: string | null;
  bookRate: number | null;
  lives: number | null;
  components: BreakoutComponent[];
  totals: CoverageTotals[];
  billingInstructions: BillingInstruction[];
}

export interface BreakoutVersionDetail extends BreakoutVersionMeta {
  snapshot: BreakoutSnapshot;
  commissionsIncluded: boolean;
}

export interface CrossCheckResult {
  tier: RateTier;
  adminRate: number | null;
  breakoutGrandTotal: number | null;
  delta: number | null;
  status: 'ok' | 'mismatch' | 'unknown';
}

export interface DestinationSplitCheck {
  component: string;
  rate: number | null;
  splitTotal: number | null;
  delta: number | null;
  status: 'ok' | 'mismatch' | 'unknown';
}

export interface PlanYearProfile {
  header: PlanYearFinancial;
  breakout: BreakoutComponent[];
  rateSets: RateSet[];
  captive: CaptiveTerms | null;
  /**
   * The group's funding method as the monitoring report uses it, mirrored from
   * Group Info "Funding Type" onto finmon.group_config by the enrollment sync.
   * Null when the group is not monitored. READ-ONLY here: the plan year has its
   * own funding_method column, nothing reads it, and the UI no longer writes it.
   */
  groupFundingMethod: FundingMethod | null;
  /**
   * Up to three prior plan years' breakout figures, newest first, read from
   * those years' own rows. Commission rates arrive masked by the same rule as
   * the current year.
   */
  priorYears: PriorYearFigures[];
  totals: CoverageTotals[];
  /**
   * Per-arrangement totals: one entry per (coverage line, scenario) for every
   * arrangement with rows in play or a breakout tab. A tab's footer reads
   * these rather than the whole-year totals, which would sum every
   * arrangement's variants at once. Masked like `totals`.
   */
  scenarioTotals: ScenarioTotals[];
  /** The year's breakout tabs (020). Empty = the year renders un-tabbed. */
  breakoutTabs: BreakoutTab[];
  billingInstructions: BillingInstruction[];
  checks: {
    crossCheck: { rateSetId: string; label: string; results: CrossCheckResult[] }[];
    destinationSplits: DestinationSplitCheck[];
    /**
     * The elected-versus-priced cross-check, compared against the snapshot the
     * stamp took rather than a live Dataverse read. Empty when no stamp has
     * run: "elects nothing" and "nobody looked" are different claims.
     */
    election: {
      asOf: string | null;
      electedNotPriced: { component: string; componentKey: string }[];
      pricedNotElected: { component: string; componentKey: string }[];
    };
    blockers: string[];
    /**
     * The tab-versus-plan cross-check (020): a plan on an unapproved
     * arrangement, or an approved tab no plan points at. Warnings, never
     * blockers. Absent from a pre-020 BFF, hence optional.
     */
    tabWarnings?: string[];
  };
  commissionsIncluded: boolean;
}

export { priorFigureFor, type PriorYearFigures } from './priorYear';

export interface BudgetLine {
  tier: RateTier;
  subscribers: number | null;
  totalRate: number | null;
  amount: number | null;
}

export interface BudgetResult {
  lines: BudgetLine[];
  budgetedAmount: number | null;
  gaps: string[];
  perSubscriber: number | null;
  perMember: number | null;
}

export interface PlanYearBudget {
  month: string;
  budgetBasis: BudgetBasis;
  sets: {
    rateSetId: string;
    label: string;
    coverageLine: CoverageLine;
    budget: BudgetResult;
    /** Why the set cannot budget this month (9/14 double-count guard); absent from an older BFF. */
    refusal?: 'needs_plan_link' | 'needs_census_by_plan' | null;
  }[];
  budgetedAmount: number | null;
}

export interface StampResult {
  ok: boolean;
  dryRun?: boolean;
  planYearFinancialId: string | null;
  servicesRead: number;
  created: number;
  adopted: number;
  skipped: number;
  refreshed: number;
  unresolved: number;
  scenario: string | null;
  lives: number | null;
  rows?: { component: string; outcome: string; rate: number | null; gap: string | null }[];
  errors: string[];
}

export interface CopyForwardResult {
  ok: boolean;
  planYearFinancialId: string | null;
  copied: number;
  skipped: number;
  errors: string[];
}

export interface LockResult {
  locked: boolean;
  blockers: string[];
}

// ---------------------------------------------------------------------------
// Masking helpers
// ---------------------------------------------------------------------------

/**
 * A coverage line whose breakout carries a masked component. Its grand total
 * and broker figures come back null BECAUSE they are gated, not because
 * nobody entered them, and the screen has to say so: an admin PEPM rendered
 * as a dash reads as a data gap and sends someone hunting for a number that
 * is right there behind a role.
 */
export function isCoverageLineWithheld(
  profile: PlanYearProfile | undefined,
  line: CoverageLine
): boolean {
  if (!profile || profile.commissionsIncluded) return false;
  return profile.breakout.some((c) => c.coverageLine === line && c.masked === true);
}

/** Same question for a derived billing-instruction bucket. */
export function isBillingLineWithheld(
  profile: PlanYearProfile | undefined,
  instruction: BillingInstruction
): boolean {
  if (!profile || profile.commissionsIncluded) return false;
  const masked = new Set(profile.breakout.filter((c) => c.masked).map((c) => c.component));
  return instruction.components.some((c) => masked.has(c));
}

/**
 * Is this row commission economics?
 *
 * Mirrors isBrokerComponent in packages/bff/src/finmon/planYearRates.ts and
 * fails closed the same way: the declared flag first, `masked` second (the
 * server only masks a commission component, so a masked row IS one even when
 * the flag did not travel), and the old keyword match last for an
 * off-vocabulary "Other" that has no flag to read.
 */
export function isCommissionRow(c: BreakoutComponent): boolean {
  if (c.isCommission === true) return true;
  if (c.masked === true) return true;
  const hay = `${c.billingLine ?? ''} ${c.component}`.toLowerCase();
  return hay.includes('broker') || hay.includes('commission');
}

/**
 * Rows a cleanup may remove: the ones nobody has put anything into.
 *
 * "Nothing entered" is stricter than "no rate", because several ways of having
 * no rate are deliberate statements rather than gaps:
 *
 *   - `settled_via` other than admin_pepm. COBRA reads "Included" on every
 *     worksheet in the corpus: agreed, carried inside the TPA fee, priced at
 *     nothing on purpose. Removing it would delete the fact that it is included.
 *   - A rate written as text ($175/hr, "billed by ProCare"), a destination
 *     split, or a prior-year figure. Each is somebody's work.
 *   - An `always` component. TPA Fee and Broker Commission are blank precisely
 *     BECAUSE they still need pricing, and they are on every EBMS group. A
 *     cleanup that removed them would delete the two rows the confirm gate
 *     exists to insist on.
 *   - Anything confirmed or locked. The BFF refuses those anyway; this keeps
 *     them out of the preview so the count never promises more than it does.
 *
 * What is left is the case this was asked for: rows the pre-009 stamp filed
 * from the service list, which are benefits rather than admin fees.
 */
export function isUnpricedRow(
  c: BreakoutComponent,
  vocabulary: FeeComponent[]
): boolean {
  if (c.status !== 'believed') return false;
  if (c.rate !== null) return false;
  if (c.rateText !== null && c.rateText.trim() !== '') return false;
  if (c.destVendor !== null || c.destTpa !== null || c.destVeracity !== null) return false;
  if (c.priorYearRate !== null) return false;
  if (c.settledVia !== 'admin_pepm') return false;
  const component = c.feeComponentId
    ? vocabulary.find((v) => v.id === c.feeComponentId)
    : undefined;
  if (component?.stampRule === 'always') return false;
  return true;
}

/**
 * The admin PEPM the group is billed: the breakout grand total summed across
 * every section (Sasha 9/02). Medical alone under-states it for any group
 * whose breakout carries Dental or Vision sections, and the claims funding
 * cross-check on the server ties to this same all-section figure.
 */
export function adminPepmOf(profile: PlanYearProfile | undefined): number | null {
  const known = (profile?.totals ?? [])
    .map((t) => t.grandTotal)
    .filter((v): v is number => v != null);
  if (known.length === 0) return null;
  return Math.round(known.reduce((s, v) => s + v, 0) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

async function readError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; blockers?: string[] };
  const detail = body.blockers?.length ? `: ${body.blockers.join('; ')}` : '';
  throw new Error(`${body.error || `${fallback} (${res.status})`}${detail}`);
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * The plan year's financial profile. 404 means nobody has priced this year
 * yet, which is a normal empty state rather than an error, so it resolves to
 * null and the screen offers Pull from Catalog.
 */
export async function fetchPlanYearProfile(contractYearId: string): Promise<PlanYearProfile | null> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(contractYearId)}`);
  if (res.status === 404) return null;
  if (!res.ok) await readError(res, 'Failed to load the plan year financials');
  return (await res.json()) as PlanYearProfile;
}

export async function fetchPlanYearBudget(contractYearId: string, month: string): Promise<PlanYearBudget | null> {
  const res = await bffFetch(
    `/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/budget/${encodeURIComponent(month)}`
  );
  if (res.status === 404) return null;
  if (!res.ok) await readError(res, 'Failed to compute the budget');
  return (await res.json()) as PlanYearBudget;
}

export interface PlanYearHeaderPatch {
  groupLabel?: string;
  planYearLabel?: string | null;
  // No fundingMethod: Group Info owns it. See PlanYearProfile.groupFundingMethod.
  budgetBasis?: BudgetBasis;
  isCaptive?: boolean;
  /** DEPRECATED for resolution (014): the proposal wording, kept as a record. */
  scenario?: string | null;
  /** The year default arrangement. Per-plan declarations override it. */
  pricingScenarioId?: string | null;
  livesAtProposal?: number | null;
  bookRate?: number | null;
  breakoutStatus?: BreakoutStatus;
  breakoutStatusNote?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  notes?: string | null;
}

export async function createPlanYearFinancial(
  input: PlanYearHeaderPatch & { accountId: string; contractYearId: string }
): Promise<PlanYearFinancial> {
  const res = await bffFetch('/api/finmon/plan-year', json(input));
  if (!res.ok) await readError(res, 'Failed to start the plan year financial record');
  return (await res.json()) as PlanYearFinancial;
}

export async function updatePlanYearFinancial(id: string, patch: PlanYearHeaderPatch): Promise<PlanYearFinancial> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(id)}`, {
    ...json(patch), method: 'PATCH',
  });
  if (!res.ok) await readError(res, 'Failed to save the plan year financials');
  return (await res.json()) as PlanYearFinancial;
}

export interface BreakoutComponentInput {
  component: string;
  /**
   * The vocabulary row. Null makes this an "Other", which the schema then
   * requires to carry a note saying what it is: an unexplained one-off is what
   * turns a controlled list back into free text.
   */
  feeComponentId?: string | null;
  /**
   * The arrangement this row prices (014/020). Null, the default, applies to
   * every arrangement; a tab's Add component sets it to add a tab-only row.
   */
  pricingScenarioId?: string | null;
  vendor?: string | null;
  coverageLine?: CoverageLine;
  basis?: FeeBasis;
  rate?: number | null;
  rateText?: string | null;
  settledVia?: SettledVia;
  billingLine?: string | null;
  destVendor?: number | null;
  destTpa?: number | null;
  destVeracity?: number | null;
  destBroker?: number | null;
  billedBy?: string | null;
  billedByPartnerId?: string | null;
  // No priorYearRate: the prior-year figure derives from the prior plan
  // year's own rows (profile.priorYears) and the stored column is read-only.
  status?: LifecycleStatus;
  displayOrder?: number;
  notes?: string | null;
}

/** The vocabulary the breakout picker offers. Open to any signed-in user. */
export async function fetchFeeComponents(): Promise<FeeComponent[]> {
  const res = await bffFetch('/api/finmon/fee-components');
  if (!res.ok) await readError(res, 'Failed to load the component catalog');
  return ((await res.json()) as { components: FeeComponent[] }).components;
}

export async function createBreakoutComponent(
  planYearFinancialId: string,
  input: BreakoutComponentInput
): Promise<BreakoutComponent> {
  const res = await bffFetch(
    `/api/finmon/plan-year/${encodeURIComponent(planYearFinancialId)}/components`,
    json(input)
  );
  if (!res.ok) await readError(res, 'Failed to add the component');
  return (await res.json()) as BreakoutComponent;
}

/**
 * A component patch plus the adjustment-log fields (021): neither is a column
 * on the row. The BFF REQUIRES changeReason when the patch moves a
 * template-linked rate off its card, and records both on the log entry.
 */
export interface BreakoutComponentPatch extends Partial<BreakoutComponentInput> {
  changeReason?: string | null;
  /** Free-text out-of-band sign-off ("per Kimberly's email"). Optional. */
  changeApprovedBy?: string | null;
}

export async function updateBreakoutComponent(
  id: string,
  patch: BreakoutComponentPatch
): Promise<BreakoutComponent> {
  const res = await bffFetch(`/api/finmon/plan-year/components/${encodeURIComponent(id)}`, {
    ...json(patch), method: 'PATCH',
  });
  if (!res.ok) await readError(res, 'Failed to save the component');
  return (await res.json()) as BreakoutComponent;
}

// ---------------------------------------------------------------------------
// The adjustment log and the off-cycle re-pull (021)
// ---------------------------------------------------------------------------

export type BreakoutChangeKind =
  | 'manual_edit' | 'repull_period' | 'stamp_refresh' | 'split' | 'copy_forward';

export const BREAKOUT_CHANGE_KIND_LABELS: Record<BreakoutChangeKind, string> = {
  manual_edit: 'Edited', repull_period: 'Re-priced from another period',
  stamp_refresh: 'Refreshed from the card', split: 'Split for one arrangement',
  copy_forward: 'Copied from the prior year',
};

export interface BreakoutChange {
  id: string;
  breakoutComponentId: string;
  planYearFinancialId: string;
  kind: BreakoutChangeKind;
  /** Only the fields that changed, keyed by field name. Null on a birth. */
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown>;
  reason: string | null;
  /** Who blessed the adjustment out of band. Not the queue approval. */
  approvedBy: string | null;
  changedBy: string;
  changedAt: string;
  masked?: boolean;
  brokerSliceWithheld?: boolean;
}

export async function fetchBreakoutChanges(componentId: string): Promise<BreakoutChange[]> {
  const res = await bffFetch(
    `/api/finmon/plan-year/components/${encodeURIComponent(componentId)}/changes`
  );
  if (!res.ok) await readError(res, 'Failed to load the adjustment log');
  return ((await res.json()) as { changes: BreakoutChange[] }).changes;
}

/** A rate card in a component's supersede chain, for the re-pull picker. */
export interface FeeCard {
  id: string;
  feeComponentId: string;
  serviceLabel: string;
  pricingScenarioId: string | null;
  scenarioKey: string | null;
  scenarioLabel: string | null;
  vendor: string | null;
  basis: FeeBasis;
  rate: number | null;
  rateTiers: unknown | null;
  bookRate: number | null;
  settledVia: SettledVia;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
}

/**
 * The component's full card history, superseded and future cards included:
 * what the re-pull picker offers. Requires Finance access, same as the writes
 * this feeds.
 */
export async function fetchFeeCardChain(feeComponentId: string): Promise<FeeCard[]> {
  const res = await bffFetch(
    `/api/finmon/fee-templates?feeComponentId=${encodeURIComponent(feeComponentId)}&includeSuperseded=true`
  );
  if (!res.ok) await readError(res, 'Failed to load the card history');
  return ((await res.json()) as { templates: FeeCard[] }).templates;
}

/**
 * Re-price one row from a specific card in its chain (the off-cycle pick).
 * The BFF validates the card belongs to the row's component and arrangement,
 * re-resolves the rate at the year's lives, and logs the pick.
 */
export async function repullBreakoutComponent(
  id: string,
  input: { feeTemplateId: string; note?: string | null; approvedBy?: string | null }
): Promise<BreakoutComponent> {
  const res = await bffFetch(
    `/api/finmon/plan-year/components/${encodeURIComponent(id)}/repull`,
    json(input)
  );
  if (!res.ok) await readError(res, 'Failed to re-price from that card');
  return (await res.json()) as BreakoutComponent;
}

export async function deleteBreakoutComponent(id: string): Promise<void> {
  const res = await bffFetch(`/api/finmon/plan-year/components/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to remove the component');
}

export interface RateSetInput {
  label: string;
  scope?: RateSet['scope'];
  planIds?: string[];
  planKeys?: string[];
  coverageLine?: CoverageLine;
  effectiveFrom: string;
  changeReason?: string | null;
  notes?: string | null;
  tiers?: {
    tier: RateTier;
    totalRate?: number | null;
    adminRate?: number | null;
    specRate?: number | null;
    aggRate?: number | null;
    fundingRate?: number | null;
    displayOrder?: number;
  }[];
}

export async function createRateSet(planYearFinancialId: string, input: RateSetInput): Promise<RateSet> {
  const res = await bffFetch(
    `/api/finmon/plan-year/${encodeURIComponent(planYearFinancialId)}/rate-sets`,
    json(input)
  );
  if (!res.ok) await readError(res, 'Failed to create the rate set');
  return (await res.json()) as RateSet;
}

export async function upsertRateSetTiers(
  rateSetId: string,
  tiers: NonNullable<RateSetInput['tiers']>
): Promise<RateSet> {
  const res = await bffFetch(`/api/finmon/plan-year/rate-sets/${encodeURIComponent(rateSetId)}/tiers`, {
    ...json({ tiers }), method: 'PUT',
  });
  if (!res.ok) await readError(res, 'Failed to save the tier rates');
  return (await res.json()) as RateSet;
}

/** A mid-year re-cut: close the current set, open a successor (Redstone). */
export async function supersedeRateSet(
  rateSetId: string,
  input: { effectiveFrom: string; changeReason?: string | null; tiers?: RateSetInput['tiers'] }
): Promise<RateSet> {
  const res = await bffFetch(
    `/api/finmon/plan-year/rate-sets/${encodeURIComponent(rateSetId)}/supersede`,
    json(input)
  );
  if (!res.ok) await readError(res, 'Failed to supersede the rate set');
  return (await res.json()) as RateSet;
}

/** The pick a rate set stores: which partner Plan ID it prices (022). */
export interface RateSetPlanRefInput {
  partnerPlanRefId: string;
  partnerPlanRefOrg: string;
  partnerPlanIdValue: string;
  partnerPlanLabel?: string | null;
}

/**
 * Set or clear the partner Plan ID on a rate set. Allowed on a locked set:
 * the lock protects the quoted figures, this is linkage metadata beside them.
 */
export async function setRateSetPlanRef(
  rateSetId: string,
  planRef: RateSetPlanRefInput | null
): Promise<RateSet> {
  const res = await bffFetch(`/api/finmon/plan-year/rate-sets/${encodeURIComponent(rateSetId)}/plan-ref`, {
    ...json({ planRef }), method: 'PUT',
  });
  if (!res.ok) await readError(res, 'Failed to set the plan ID');
  return (await res.json()) as RateSet;
}

/**
 * Link the group plans a plan-scoped rate set prices (9/14). Allowed on a
 * locked set: which plan's enrollment the rates multiply is linkage, not a rate.
 */
export async function setRateSetPlans(rateSetId: string, planIds: string[], planKeys: string[]): Promise<RateSet> {
  const res = await bffFetch(`/api/finmon/plan-year/rate-sets/${encodeURIComponent(rateSetId)}/plans`, {
    ...json({ planIds, planKeys }), method: 'PUT',
  });
  if (!res.ok) await readError(res, 'Failed to link the plans');
  return (await res.json()) as RateSet;
}

export interface CaptiveTermsInput {
  captiveName?: string | null;
  managementFee?: number | null;
  managementFeeCadence?: string;
  collateral?: number | null;
  collateralCadence?: string;
  expectedFirstBilledMonth?: string | null;
  inBudgetRates?: boolean | null;
  effectiveFrom: string;
  notes?: string | null;
}

export async function upsertCaptiveTerms(
  planYearFinancialId: string,
  input: CaptiveTermsInput
): Promise<CaptiveTerms> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(planYearFinancialId)}/captive`, {
    ...json(input), method: 'PUT',
  });
  if (!res.ok) await readError(res, 'Failed to save the captive terms');
  return (await res.json()) as CaptiveTerms;
}

export async function stampBreakoutFromCatalog(
  contractYearId: string,
  input: { accountId: string; groupLabel?: string; lives?: number; scenario?: string; refreshBelieved?: boolean; dryRun?: boolean; org?: string }
): Promise<StampResult> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/stamp`, json(input));
  if (!res.ok) await readError(res, 'Pull from Catalog failed');
  return (await res.json()) as StampResult;
}

export async function copyForwardBreakout(
  contractYearId: string,
  input: { accountId: string; priorContractYearId: string; groupLabel?: string }
): Promise<CopyForwardResult> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/copy-forward`, json(input));
  if (!res.ok) await readError(res, 'Copy from Prior Year failed');
  return (await res.json()) as CopyForwardResult;
}

/**
 * Confirm and lock. A 409 carries the blockers, which the screen shows as a
 * list rather than a failure: the gate refusing is the feature.
 */
export async function lockPlanYear(contractYearId: string, force = false): Promise<LockResult> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/lock`, json({ force }));
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { blockers?: string[] };
    return { locked: false, blockers: body.blockers ?? [] };
  }
  if (!res.ok) await readError(res, 'Failed to lock the plan year');
  return (await res.json()) as LockResult;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useFeeComponents() {
  return useQuery({
    queryKey: ['ops-finmon', 'fee-components'] as const,
    queryFn: fetchFeeComponents,
    staleTime: 5 * 60 * 1000,
    // Fail soft: without the vocabulary the dialog falls back to Other, which
    // still records what someone needed rather than blocking them.
    retry: false,
  });
}

const profileKey = (contractYearId: string) =>
  ['ops-finmon', 'plan-year-financial', contractYearId] as const;

export function usePlanYearFinancial(contractYearId: string | undefined) {
  return useQuery({
    queryKey: profileKey(contractYearId ?? ''),
    queryFn: () => fetchPlanYearProfile(contractYearId as string),
    enabled: !!contractYearId,
    // Fail soft the same way the fee-structure roster does: an env where the
    // plan-year routes are not deployed yet must not error-spam the tab.
    retry: false,
  });
}

export function usePlanYearBudget(contractYearId: string | undefined, month: string | undefined) {
  return useQuery({
    queryKey: ['ops-finmon', 'plan-year-budget', contractYearId ?? '', month ?? ''] as const,
    queryFn: () => fetchPlanYearBudget(contractYearId as string, month as string),
    enabled: !!contractYearId && !!month,
    retry: false,
  });
}

/**
 * Every mutation on this screen invalidates the one profile query: the profile
 * carries the derived roll-ups, the cross-check and the blockers, so a write
 * that did not refresh it would leave a stale grand total beside a fresh row.
 */
function useProfileMutation<TArgs, TResult>(
  contractYearId: string | undefined,
  fn: (args: TArgs) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey(contractYearId ?? '') });
      queryClient.invalidateQueries({ queryKey: ['ops-finmon', 'plan-year-budget', contractYearId ?? ''] });
      // Writes feed the adjustment log (021); a stale history panel beside a
      // fresh row would say the edit never happened.
      queryClient.invalidateQueries({ queryKey: ['ops-finmon', 'breakout-changes'] });
    },
  });
}

export function useCreatePlanYearFinancial(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, createPlanYearFinancial);
}

export function useUpdatePlanYearFinancial(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { id: string; patch: PlanYearHeaderPatch }) =>
    updatePlanYearFinancial(args.id, args.patch)
  );
}

export function useCreateBreakoutComponent(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { planYearFinancialId: string; input: BreakoutComponentInput }) =>
    createBreakoutComponent(args.planYearFinancialId, args.input)
  );
}

export function useUpdateBreakoutComponent(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { id: string; patch: BreakoutComponentPatch }) =>
    updateBreakoutComponent(args.id, args.patch)
  );
}

/** One row's adjustment log, loaded when its history panel opens. */
export function useBreakoutChanges(componentId: string | undefined) {
  return useQuery({
    queryKey: ['ops-finmon', 'breakout-changes', componentId ?? ''] as const,
    queryFn: () => fetchBreakoutChanges(componentId as string),
    enabled: !!componentId,
    retry: false,
  });
}

/** The card chain for the re-pull picker, loaded when the picker opens. */
export function useFeeCardChain(feeComponentId: string | undefined) {
  return useQuery({
    queryKey: ['ops-finmon', 'fee-card-chain', feeComponentId ?? ''] as const,
    queryFn: () => fetchFeeCardChain(feeComponentId as string),
    enabled: !!feeComponentId,
    retry: false,
  });
}

export function useRepullBreakoutComponent(contractYearId: string | undefined) {
  return useProfileMutation(
    contractYearId,
    (args: { id: string; feeTemplateId: string; note?: string | null; approvedBy?: string | null }) =>
      repullBreakoutComponent(args.id, {
        feeTemplateId: args.feeTemplateId, note: args.note, approvedBy: args.approvedBy,
      })
  );
}

export function useDeleteBreakoutComponent(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { id: string }) => deleteBreakoutComponent(args.id));
}

export function useCreateRateSet(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { planYearFinancialId: string; input: RateSetInput }) =>
    createRateSet(args.planYearFinancialId, args.input)
  );
}

export function useUpsertRateSetTiers(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { rateSetId: string; tiers: NonNullable<RateSetInput['tiers']> }) =>
    upsertRateSetTiers(args.rateSetId, args.tiers)
  );
}

export function useSupersedeRateSet(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { rateSetId: string; input: Parameters<typeof supersedeRateSet>[1] }) =>
    supersedeRateSet(args.rateSetId, args.input)
  );
}

export function useSetRateSetPlanRef(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { rateSetId: string; planRef: RateSetPlanRefInput | null }) =>
    setRateSetPlanRef(args.rateSetId, args.planRef)
  );
}

export function useSetRateSetPlans(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { rateSetId: string; planIds: string[]; planKeys: string[] }) =>
    setRateSetPlans(args.rateSetId, args.planIds, args.planKeys)
  );
}

export function useUpsertCaptiveTerms(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { planYearFinancialId: string; input: CaptiveTermsInput }) =>
    upsertCaptiveTerms(args.planYearFinancialId, args.input)
  );
}

export function useStampBreakout(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: Parameters<typeof stampBreakoutFromCatalog>[1]) =>
    stampBreakoutFromCatalog(contractYearId as string, args)
  );
}

export function useCopyForwardBreakout(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: Parameters<typeof copyForwardBreakout>[1]) =>
    copyForwardBreakout(contractYearId as string, args)
  );
}

/**
 * Remove several rows in one go. Deletes sequentially through the same audited
 * endpoint a single delete uses, and reports partial failure rather than
 * pretending the whole thing worked: a row that has been confirmed since the
 * preview was computed will refuse, and saying so beats a silent shortfall.
 */
export function useBulkDeleteBreakoutComponents(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, async (args: { ids: string[] }) => {
    let removed = 0;
    const failed: string[] = [];
    for (const id of args.ids) {
      try {
        await deleteBreakoutComponent(id);
        removed++;
      } catch (e) {
        failed.push((e as Error).message);
      }
    }
    return { removed, failed };
  });
}

export function useLockPlanYear(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, (args: { force?: boolean }) =>
    lockPlanYear(contractYearId as string, args.force === true)
  );
}

// ---------------------------------------------------------------------------
// The hand-kept budgeted census (migration 012). Only read when the plan
// year's budget basis is budgeted_census; the card that edits it renders only
// then, so the query is gated the same way.
// ---------------------------------------------------------------------------

export interface BudgetCensusRow {
  id: string;
  planYearFinancialId: string;
  censusMonth: string; // first of month, YYYY-MM-DD
  planId: string | null;
  tier: RateTier;
  subscribers: number;
  members: number | null;
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

export interface BudgetCensusEntry {
  tier: RateTier;
  subscribers: number;
  members?: number | null;
}

export async function fetchBudgetCensus(contractYearId: string): Promise<BudgetCensusRow[]> {
  const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/budget-census`);
  if (res.status === 404) return [];
  if (!res.ok) await readError(res, 'Failed to load the budgeted census');
  return ((await res.json()) as { rows: BudgetCensusRow[] }).rows;
}

/** Replace one month's tier counts for one plan scope (null = group total). */
export async function replaceBudgetCensusMonth(
  contractYearId: string,
  month: string,
  planId: string | null,
  entries: BudgetCensusEntry[]
): Promise<BudgetCensusRow[]> {
  const res = await bffFetch(
    `/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/budget-census/${encodeURIComponent(month)}`,
    { ...json({ planId, entries }), method: 'PUT' }
  );
  if (!res.ok) await readError(res, 'Failed to save the budgeted census month');
  return ((await res.json()) as { rows: BudgetCensusRow[] }).rows;
}

export async function deleteBudgetCensusMonth(
  contractYearId: string,
  month: string,
  planId: string | null
): Promise<number> {
  const qs = planId ? `?planId=${encodeURIComponent(planId)}` : '';
  const res = await bffFetch(
    `/api/finmon/plan-year/${encodeURIComponent(contractYearId)}/budget-census/${encodeURIComponent(month)}${qs}`,
    { method: 'DELETE' }
  );
  if (!res.ok) await readError(res, 'Failed to remove the budgeted census month');
  return ((await res.json()) as { removed: number }).removed;
}

const budgetCensusKey = (contractYearId: string) =>
  ['ops-finmon', 'budget-census', contractYearId] as const;

export function useBudgetCensus(contractYearId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: budgetCensusKey(contractYearId ?? ''),
    queryFn: () => fetchBudgetCensus(contractYearId as string),
    enabled: !!contractYearId && enabled,
    retry: false,
  });
}

/** Budget census writes also refresh the budget the census feeds. */
function useBudgetCensusMutation<TArgs, TResult>(
  contractYearId: string | undefined,
  fn: (args: TArgs) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetCensusKey(contractYearId ?? '') });
      queryClient.invalidateQueries({ queryKey: ['ops-finmon', 'plan-year-budget', contractYearId ?? ''] });
    },
  });
}

export function useReplaceBudgetCensusMonth(contractYearId: string | undefined) {
  return useBudgetCensusMutation(contractYearId,
    (args: { month: string; planId: string | null; entries: BudgetCensusEntry[] }) =>
      replaceBudgetCensusMonth(contractYearId as string, args.month, args.planId, args.entries)
  );
}

export function useDeleteBudgetCensusMonth(contractYearId: string | undefined) {
  return useBudgetCensusMutation(contractYearId,
    (args: { month: string; planId: string | null }) =>
      deleteBudgetCensusMonth(contractYearId as string, args.month, args.planId)
  );
}

// ---------------------------------------------------------------------------
// The observed census (finmon.census), typed per month into a rate set's
// enrollment grid (9/14). The same rows the monitoring report's budget reads:
// the agg summary fills group totals, and a typed count wins over any feed.
// ---------------------------------------------------------------------------

export interface CensusRow {
  id: string;
  accountId: string;
  censusMonth: string; // first of month, YYYY-MM-DD
  planId: string | null;
  planKey: string | null;
  planLabel: string | null;
  tier: RateTier;
  subscribersCalculated: number | null;
  membersCalculated: number | null;
  calculatedSource: string;
  subscribersControl: number | null;
  controlSource: string | null;
  /** The feed value a typed count replaced, kept so clearing can put it back. */
  originalSubscribers: number | null;
  originalSource: string | null;
  matchStatus: string;
  overriddenBy: string | null;
  overriddenAt: string | null;
}

export interface CensusMonthEntry {
  tier: RateTier;
  /** Null clears a typed count; a feed value is never cleared from here. */
  subscribers: number | null;
}

export interface CensusMonthResult {
  written: number;
  cleared: number;
  unchanged: number;
  flagged: number;
}

export async function fetchCensus(accountId: string, from: string | null): Promise<CensusRow[]> {
  const qs = from ? `?from=${encodeURIComponent(from.slice(0, 7))}` : '';
  const res = await bffFetch(`/api/finmon/census/${encodeURIComponent(accountId)}${qs}`);
  if (res.status === 404) return [];
  if (!res.ok) await readError(res, 'Failed to load the census');
  return ((await res.json()) as { rows: CensusRow[] }).rows;
}

/** One month's tier counts for one plan scope (planId null = group total). */
export async function saveCensusMonth(
  accountId: string,
  month: string,
  input: { planId: string | null; planKey?: string | null; planLabel?: string | null; entries: CensusMonthEntry[] }
): Promise<CensusMonthResult> {
  const res = await bffFetch(
    `/api/finmon/census/${encodeURIComponent(accountId)}/${encodeURIComponent(month.slice(0, 7))}/tiers`,
    { ...json(input), method: 'PUT' }
  );
  if (!res.ok) await readError(res, 'Failed to save the enrollment');
  return (await res.json()) as CensusMonthResult;
}

const censusKey = (accountId: string) => ['ops-finmon', 'census', accountId] as const;

export function useCensus(accountId: string | undefined, from: string | null) {
  return useQuery({
    queryKey: [...censusKey(accountId ?? ''), from ?? ''] as const,
    queryFn: () => fetchCensus(accountId as string, from),
    enabled: !!accountId,
    retry: false,
  });
}

/** A typed month refreshes the grid and the budget the census feeds. */
export function useSaveCensusMonth(accountId: string | undefined, contractYearId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      month: string;
      planId: string | null;
      planKey?: string | null;
      planLabel?: string | null;
      entries: CensusMonthEntry[];
    }) =>
      saveCensusMonth(accountId as string, args.month, {
        planId: args.planId, planKey: args.planKey, planLabel: args.planLabel, entries: args.entries,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: censusKey(accountId ?? '') });
      queryClient.invalidateQueries({ queryKey: ['ops-finmon', 'plan-year-budget', contractYearId ?? ''] });
    },
  });
}

// ---------------------------------------------------------------------------
// Pricing scenarios and the plan-to-scenario map (migration 014)
//
// Mirrors packages/finance/src/api/{finance,useFinance}.ts. Ops keeps its own
// copy for the same reason as everything above: modules do not import each
// other here. Keep the two in step.
//
// The arrangement is what decides which rate card a component resolves against,
// and a group can run several at once because the member picks at enrollment.
// ---------------------------------------------------------------------------

export type ScenarioKind = 'network' | 'funding' | 'bundle';

export interface PricingScenario {
  id: string;
  scenarioKey: string;
  label: string;
  kind: ScenarioKind;
  active: boolean;
  displayOrder: number;
  notes: string | null;
  cardCount?: number;
}

export interface PlanScenario {
  id: string;
  planYearFinancialId: string;
  planKey: string;
  planId: string | null;
  planLabel: string | null;
  pricingScenarioId: string;
  scenarioKey: string;
  scenarioLabel: string;
  status: LifecycleStatus;
  source: string;
  notes: string | null;
}

export function usePricingScenarios() {
  return useQuery({
    queryKey: ['ops', 'pricing-scenarios'],
    queryFn: async (): Promise<PricingScenario[]> => {
      const res = await bffFetch('/api/finmon/pricing-scenarios');
      if (!res.ok) throw new Error('Failed to load the pricing scenarios');
      return ((await res.json()) as { scenarios: PricingScenario[] }).scenarios;
    },
    staleTime: 10 * 60_000,
  });
}

export function usePlanScenarios(headerId: string | undefined) {
  return useQuery({
    queryKey: ['ops', 'plan-scenarios', headerId],
    queryFn: async (): Promise<PlanScenario[]> => {
      const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(headerId as string)}/plan-scenarios`);
      if (!res.ok) throw new Error('Failed to load the plan arrangements');
      return ((await res.json()) as { planScenarios: PlanScenario[] }).planScenarios;
    },
    enabled: !!headerId,
    staleTime: 60_000,
  });
}

/**
 * Declaring an arrangement changes which card the stamp resolves, so the plan
 * year profile is re-read with it: the breakout, its per-arrangement totals and
 * the cross-check all move together.
 */
export function useUpsertPlanScenario(headerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      planKey: string;
      pricingScenarioId: string;
      planId?: string | null;
      planLabel?: string | null;
      status?: LifecycleStatus;
      notes?: string | null;
    }): Promise<PlanScenario> => {
      const res = await bffFetch(`/api/finmon/plan-year/${encodeURIComponent(headerId as string)}/plan-scenarios`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await readErr(res, 'Failed to save the arrangement'));
      return (await res.json()) as PlanScenario;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'plan-scenarios', headerId] });
      void qc.invalidateQueries({ queryKey: ['ops', 'plan-year'] });
    },
  });
}

export function useDeletePlanScenario(headerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await bffFetch(`/api/finmon/plan-scenarios/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readErr(res, 'Failed to remove the arrangement'));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'plan-scenarios', headerId] });
      void qc.invalidateQueries({ queryKey: ['ops', 'plan-year'] });
    },
  });
}

async function readErr(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? fallback;
  } catch {
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Breakout tabs and versions (migration 020)
//
// Mirrors packages/bff/src/finmon/breakoutTabs.ts. Tab writes invalidate the
// profile (the tabs, the per-scenario totals and the tab warnings all live on
// it) plus the tab's version list, since status changes mint versions.
// ---------------------------------------------------------------------------

/**
 * The Billing Instructions roll-up for a FILTERED view (a tab): the same rule
 * the server applies to the whole year (planYearRates.rollUpBillingLines),
 * restated over the already-masked rows a tab shows. A masked commission rate
 * is null and contributes nothing here; isBillingLineWithheld is what tells
 * the screen the bucket is gated rather than short.
 */
export function billingInstructionsFor(components: BreakoutComponent[]): BillingInstruction[] {
  const buckets = new Map<string | null, BillingInstruction>();
  for (const c of components) {
    if (c.settledVia !== 'admin_pepm') continue;
    const label = c.billingLine?.trim() || null;
    let bucket = buckets.get(label);
    if (!bucket) {
      bucket = { billingLine: label, amount: null, components: [] };
      buckets.set(label, bucket);
    }
    bucket.components.push(c.component);
    if (c.rate != null) {
      bucket.amount = Math.round(((bucket.amount ?? 0) + c.rate) * 100) / 100;
    }
  }
  return [...buckets.values()].sort((a, b) => {
    if (a.billingLine === null) return b.billingLine === null ? 0 : 1;
    if (b.billingLine === null) return -1;
    return a.billingLine.localeCompare(b.billingLine);
  });
}

export interface BreakoutTabInput {
  pricingScenarioId: string;
  displayName: string;
  bookRate?: number | null;
  statusNote?: string | null;
}

export interface BreakoutTabPatch {
  displayName?: string;
  bookRate?: number | null;
  status?: BreakoutStatus;
  statusNote?: string | null;
  archived?: boolean;
}

export interface BreakoutTabUpdateResult {
  tab: BreakoutTab;
  /**
   * The workflow event the PATCH implied, when it did: leaving
   * pending_review = 'withdrawn', leaving approved = 'unapproved' (which the
   * BFF allows only to an approver). Versions mint on Save version, Submit
   * and Approve only (9/04), so a plain status advance carries no version.
   */
  reviewAction: 'withdrawn' | 'unapproved' | null;
}

const versionsKey = (tabId: string) => ['ops-finmon', 'breakout-versions', tabId] as const;

function useTabMutation<TArgs, TResult>(
  contractYearId: string | undefined,
  fn: (args: TArgs) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey(contractYearId ?? '') });
      queryClient.invalidateQueries({ queryKey: ['ops-finmon', 'breakout-versions'] });
    },
  });
}

export function useCreateBreakoutTab(contractYearId: string | undefined) {
  return useTabMutation(contractYearId, async (args: { headerId: string; input: BreakoutTabInput }) => {
    const res = await bffFetch(
      `/api/finmon/plan-year/${encodeURIComponent(args.headerId)}/breakout-tabs`,
      json(args.input)
    );
    if (!res.ok) await readError(res, 'Failed to create the tab');
    return (await res.json()) as BreakoutTab;
  });
}

export function useUpdateBreakoutTab(contractYearId: string | undefined) {
  return useTabMutation(contractYearId, async (args: { id: string; patch: BreakoutTabPatch }) => {
    const res = await bffFetch(`/api/finmon/breakout-tabs/${encodeURIComponent(args.id)}`, {
      ...json(args.patch), method: 'PATCH',
    });
    if (!res.ok) await readError(res, 'Failed to save the tab');
    return (await res.json()) as BreakoutTabUpdateResult;
  });
}

export function useDeleteBreakoutTab(contractYearId: string | undefined) {
  return useTabMutation(contractYearId, async (args: { id: string }) => {
    const res = await bffFetch(`/api/finmon/breakout-tabs/${encodeURIComponent(args.id)}`, {
      method: 'DELETE',
    });
    if (!res.ok) await readError(res, 'Failed to remove the tab');
  });
}

/** The manual Save version. Status changes and approval mint their own. */
export function useSaveBreakoutVersion(contractYearId: string | undefined) {
  return useTabMutation(contractYearId, async (args: { tabId: string; label?: string | null }) => {
    const res = await bffFetch(
      `/api/finmon/breakout-tabs/${encodeURIComponent(args.tabId)}/versions`,
      json({ label: args.label ?? null })
    );
    if (!res.ok) await readError(res, 'Failed to save a version');
    return (await res.json()) as BreakoutVersionMeta;
  });
}

/**
 * Split a shared row for one arrangement: the edited row takes the tab's
 * scenario and the new values, and every other live tab gets a copy of the
 * original, so a split never deletes the component from the other tabs.
 */
export function useSplitBreakoutComponent(contractYearId: string | undefined) {
  return useProfileMutation(contractYearId, async (args: {
    id: string; pricingScenarioId: string; patch: Partial<BreakoutComponentInput>;
  }) => {
    const res = await bffFetch(
      `/api/finmon/plan-year/components/${encodeURIComponent(args.id)}/split`,
      json({ pricingScenarioId: args.pricingScenarioId, patch: args.patch })
    );
    if (!res.ok) await readError(res, 'Failed to split the component');
    return (await res.json()) as BreakoutComponent;
  });
}

export function useBreakoutVersions(tabId: string | undefined) {
  return useQuery({
    queryKey: versionsKey(tabId ?? ''),
    queryFn: async (): Promise<BreakoutVersionMeta[]> => {
      const res = await bffFetch(`/api/finmon/breakout-tabs/${encodeURIComponent(tabId as string)}/versions`);
      if (!res.ok) await readError(res, 'Failed to load the version history');
      return ((await res.json()) as { versions: BreakoutVersionMeta[] }).versions;
    },
    enabled: !!tabId,
    retry: false,
  });
}

/** One version with its snapshot, masked server-side per capability. */
export function useBreakoutVersion(versionId: string | undefined) {
  return useQuery({
    queryKey: ['ops-finmon', 'breakout-version', versionId ?? ''] as const,
    queryFn: async (): Promise<BreakoutVersionDetail> => {
      const res = await bffFetch(`/api/finmon/breakout-versions/${encodeURIComponent(versionId as string)}`);
      if (!res.ok) await readError(res, 'Failed to load the version');
      return (await res.json()) as BreakoutVersionDetail;
    },
    enabled: !!versionId,
    // Immutable by design, so never refetch a loaded snapshot.
    staleTime: Infinity,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// The approval workflow (migration 023). Submit rides adminfee.edit; approve,
// return and the queue read ride adminfee.approve (AdminFee.Approver).
// ---------------------------------------------------------------------------

export type ReviewAction = 'submitted' | 'approved' | 'returned' | 'withdrawn' | 'unapproved';

export const REVIEW_ACTION_LABELS: Record<ReviewAction, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
  returned: 'Returned',
  withdrawn: 'Withdrawn',
  unapproved: 'Re-opened',
};

export interface BreakoutReviewEvent {
  id: string;
  planYearScenarioId: string;
  action: ReviewAction;
  note: string | null;
  breakoutVersionId: string | null;
  versionNo: number | null;
  actor: string;
  createdAt: string;
}

export interface SubmitResult {
  tab: BreakoutTab;
  /** The snapshot the reviewer is asked to approve. */
  version: BreakoutVersionMeta;
  /** True when nothing material moved and the latest version was reused. */
  versionReused: boolean;
  /** True when the submit auto-created an un-tabbed year's default tab. */
  tabCreated: boolean;
}

export interface ApprovalQueueItem {
  tabId: string;
  displayName: string;
  scenarioLabel: string;
  headerId: string;
  accountId: string;
  contractYearId: string;
  groupLabel: string;
  planYearLabel: string | null;
  submittedBy: string | null;
  submittedAt: string | null;
  submitNote: string | null;
  submittedVersionNo: number | null;
  deviationCount: number;
  offCycleCount: number;
  editsSinceSubmit: number;
}

export const approvalsKey = ['ops-finmon', 'approvals'] as const;
export const approvalsCountKey = ['ops-finmon', 'approvals-count'] as const;

const reviewEventsKey = (tabId: string) => ['ops-finmon', 'review-events', tabId] as const;

/** The tab's decision history, newest first. */
export function useReviewEvents(tabId: string | undefined) {
  return useQuery({
    queryKey: reviewEventsKey(tabId ?? ''),
    queryFn: async (): Promise<BreakoutReviewEvent[]> => {
      const res = await bffFetch(
        `/api/finmon/breakout-tabs/${encodeURIComponent(tabId as string)}/review-events`
      );
      if (!res.ok) await readError(res, 'Failed to load the review history');
      return ((await res.json()) as { events: BreakoutReviewEvent[] }).events;
    },
    enabled: !!tabId,
    retry: false,
  });
}

/**
 * Workflow mutations invalidate the whole ops-finmon namespace: an approval
 * moves the profile, the tabs, the versions, the events, the queue and the
 * badge at once, and these are rare enough that a broad refetch is the
 * simple correct answer.
 */
function useWorkflowMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ops-finmon'] });
    },
  });
}

export function useSubmitBreakoutTab() {
  return useWorkflowMutation(async (args: { tabId: string; note?: string | null }) => {
    const res = await bffFetch(
      `/api/finmon/breakout-tabs/${encodeURIComponent(args.tabId)}/submit`,
      json({ note: args.note ?? null })
    );
    if (!res.ok) await readError(res, 'Failed to submit the tab');
    return (await res.json()) as SubmitResult;
  });
}

/** Submit an un-tabbed year: the BFF auto-creates the default tab. */
export function useSubmitPlanYear() {
  return useWorkflowMutation(async (args: { headerId: string; note?: string | null }) => {
    const res = await bffFetch(
      `/api/finmon/plan-year/${encodeURIComponent(args.headerId)}/submit`,
      json({ note: args.note ?? null })
    );
    if (!res.ok) await readError(res, 'Failed to submit for approval');
    return (await res.json()) as SubmitResult;
  });
}

export function useApproveBreakoutTab() {
  return useWorkflowMutation(async (args: { tabId: string; note?: string | null }) => {
    const res = await bffFetch(
      `/api/finmon/breakout-tabs/${encodeURIComponent(args.tabId)}/approve`,
      json({ note: args.note ?? null })
    );
    if (!res.ok) await readError(res, 'Failed to approve the tab');
    return (await res.json()) as {
      tab: BreakoutTab; finalVersion: BreakoutVersionMeta; rowsConfirmed: number;
    };
  });
}

export function useReturnBreakoutTab() {
  return useWorkflowMutation(async (args: { tabId: string; note: string }) => {
    const res = await bffFetch(
      `/api/finmon/breakout-tabs/${encodeURIComponent(args.tabId)}/return`,
      json({ note: args.note })
    );
    if (!res.ok) await readError(res, 'Failed to return the tab');
    return (await res.json()) as { tab: BreakoutTab };
  });
}

/** The cross-group queue. Only called when the viewer holds adminfee.approve. */
export function useApprovalQueue(enabled: boolean) {
  return useQuery({
    queryKey: approvalsKey,
    queryFn: async (): Promise<ApprovalQueueItem[]> => {
      const res = await bffFetch('/api/finmon/approvals');
      if (!res.ok) await readError(res, 'Failed to load the approval queue');
      return ((await res.json()) as { items: ApprovalQueueItem[] }).items;
    },
    enabled,
    retry: false,
  });
}

/** The nav badge number; shares nothing but the count so it stays cheap. */
export function useApprovalsCount(enabled: boolean) {
  return useQuery({
    queryKey: approvalsCountKey,
    queryFn: async (): Promise<number> => {
      const res = await bffFetch('/api/finmon/approvals/count');
      if (!res.ok) await readError(res, 'Failed to load the approvals count');
      return ((await res.json()) as { count: number }).count;
    },
    enabled,
    refetchInterval: 120_000,
    retry: false,
  });
}
